import React, { useEffect, useMemo, useRef, useState } from "react";

// Reuse the enhanced styles from single-select dropdown for consistent UI/UX
const enhancedStyles = `
  .rotate-180 {
    transform: rotate(180deg) !important;
  }
  .dropdown-fade-in {
    animation: fadeIn 0.15s ease-out;
  }
  .dropdown-fade-out {
    animation: fadeOut 0.1s ease-out;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeOut {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-4px); }
  }
  .searchable-dropdown-item { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
  .searchable-dropdown-item:hover { transform: translateX(2px); }
  .searchable-dropdown-selected {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
  }
  .searchable-dropdown-highlighted {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
    transform: translateX(2px);
  }
  .searchable-dropdown-input:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    border-color: #007bff;
  }
  .searchable-dropdown-main {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid #ced4da;
  }
  .searchable-dropdown-main:hover:not(:disabled) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.1);
  }
  .searchable-dropdown-main:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
  .searchable-dropdown-chevron {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .searchable-dropdown-loading { animation: pulse 1.5s ease-in-out infinite; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('searchable-dropdown-enhanced-styles')) {
  const style = document.createElement('style');
  style.id = 'searchable-dropdown-enhanced-styles';
  style.textContent = enhancedStyles;
  document.head.appendChild(style);
}

const SearchableUserTypeMultiSelect = ({
  userTypes = [],
  values = [],
  onChange,
  placeholder = "Select User Types",
  disabled = false,
  loading = false,
  maxHeight = "260px",
  filterActiveOnly = true,
  className = "form-control form-control-sm",
  showCount = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filtered = useMemo(() => {
    let list = filterActiveOnly ? userTypes.filter(t => t.active_status === true) : userTypes;
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(t => String(t.sub_category_name_view).toLowerCase().includes(q));
  }, [userTypes, searchTerm, filterActiveOnly]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const toggle = () => {
    if (disabled || loading) return;
    setIsOpen(p => !p);
  };

  const isSelected = (id) => values.some(v => String(v) === String(id));

  const handleToggleItem = (id) => {
    if (!onChange) return;
    const exists = isSelected(id);
    const next = exists ? values.filter(v => String(v) !== String(id)) : [...values, id];
    onChange(next);
  };

  const handleSelectAllFiltered = () => {
    if (!onChange) return;
    const ids = filtered.map(t => t.sub_category_id);
    onChange(ids);
  };

  const handleClearAll = () => {
    if (!onChange) return;
    onChange([]);
  };

  const label = values.length === 0 ? placeholder : `${values.length} selected`;

  return (
    <div className="position-relative" ref={dropdownRef}>
      <div
        className={`searchable-dropdown-main ${className} ${isOpen ? 'show' : ''} ${loading ? 'searchable-dropdown-loading' : ''}`}
        onClick={toggle}
        style={{
          cursor: disabled || loading ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "38px",
          paddingLeft: 12,
          paddingRight: 40,
          backgroundColor: disabled ? "#e9ecef" : loading ? "#f8f9fa" : "#fff",
          opacity: disabled ? 0.65 : loading ? 0.8 : 1,
          position: 'relative',
          borderRadius: '0.375rem'
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{loading ? "Loading..." : label}</span>
        <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <i className={`searchable-dropdown-chevron fas fa-chevron-down ${isOpen ? 'rotate-180' : ''}`} style={{ fontSize: 12, color: '#6c757d' }}></i>
        </div>
      </div>
      {isOpen && (
        <div
          className="position-absolute w-100 border border-top-0 rounded-bottom dropdown-fade-in"
          style={{
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: "#fff",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            maxHeight,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className="p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
            {/* Search row */}
            <div className="position-relative mb-2">
              <input
                ref={searchInputRef}
                type="text"
                className="searchable-dropdown-input form-control form-control-sm"
                placeholder="Type to search user types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: 32, paddingRight: 8 }}
              />
              <i className="fas fa-search position-absolute" style={{ left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13 }}></i>
            </div>
            {/* Actions row */}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={handleClearAll} title="Clear selection">Clear</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSelectAllFiltered} title="Select all filtered">Select All</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
            {showCount && (
              <div className="px-3 py-2 small text-muted d-flex align-items-center justify-content-between" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', fontWeight: 500 }}>
                <span>
                  <i className="fas fa-list me-1" style={{ fontSize: 10 }}></i>
                  {searchTerm ? `Found ${filtered.length} user type${filtered.length !== 1 ? 's' : ''}` : `All ${filtered.length} user types`}
                </span>
                {searchTerm && (
                  <button type="button" className="btn btn-sm p-0" onClick={() => setSearchTerm("")} style={{ fontSize: 10, lineHeight: 1, backgroundColor: 'transparent', border: 'none', color: '#6c757d', padding: 2 }}>Clear</button>
                )}
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-5 text-center text-muted" style={{ minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div className="mb-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-search" style={{ opacity: 0.4, fontSize: 20, color: '#6c757d' }}></i>
                </div>
                <div className="fw-medium mb-1" style={{ fontSize: '0.9rem' }}>{searchTerm ? 'No matching user types' : 'No user types available'}</div>
              </div>
            ) : (
              filtered.map((t) => {
                const checked = isSelected(t.sub_category_id);
                return (
                  <div
                    key={t.sub_category_id}
                    className={`searchable-dropdown-item px-3 py-2 d-flex align-items-center ${checked ? 'searchable-dropdown-selected text-white' : ''}`}
                    style={{ cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f1f3f4', position: 'relative' }}
                    onClick={() => handleToggleItem(t.sub_category_id)}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={checked}
                      onChange={() => handleToggleItem(t.sub_category_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="flex-grow-1" style={{ marginLeft: checked ? 8 : 0 }}>{t.sub_category_name_view}</span>
                    {checked && (
                      <div className="d-flex align-items-center" style={{ marginLeft: 8 }}>
                        <i className="fas fa-check" style={{ fontSize: 12, color: '#fff', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: 4, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}></i>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableUserTypeMultiSelect;


